import { useState, useCallback } from 'react';

/**
 * useConfirm 커스텀 훅
 * - Confirm 컴포넌트의 상태 관리를 위한 공용 훅
 * - 반복되는 Confirm 다이얼로그 로직을 재사용 가능하게 추상화
 *
 * @returns {Object} confirmState - Confirm 상태 객체 (isOpen, message, onConfirm)
 * @returns {Function} showConfirm - Confirm 다이얼로그 표시 함수
 * @returns {Function} closeConfirm - Confirm 다이얼로그 닫기 함수
 *
 * @example
 * const { confirmState, showConfirm, closeConfirm } = useConfirm();
 * showConfirm('삭제하시겠습니까?', () => { handleDelete(); closeConfirm(); });
 * <Confirm
 *   isOpen={confirmState.isOpen}
 *   message={confirmState.message}
 *   onConfirm={confirmState.onConfirm}
 *   onCancel={closeConfirm}
 * />
 */
function useConfirm() {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    message: '',
    onConfirm: null,
  });

  /**
   * Confirm 다이얼로그 표시 함수
   * @param {string} message - 표시할 확인 메시지
   * @param {Function} onConfirm - 확인 버튼 클릭 시 실행할 콜백 함수
   */
  const showConfirm = useCallback(function (message, onConfirm) {
    setConfirmState({
      isOpen: true,
      message,
      onConfirm,
    });
  }, []);

  /**
   * Confirm 다이얼로그 닫기 함수
   */
  const closeConfirm = useCallback(function () {
    setConfirmState({
      isOpen: false,
      message: '',
      onConfirm: null,
    });
  }, []);

  return {
    confirmState,
    showConfirm,
    closeConfirm,
  };
}

export default useConfirm;
